/**
 * @file: This file holds the two pure rules of a takeover.
 *
 * A takeover is what runs on a scene while one act plays. The RF26 data shows
 * how the operators built one. They copied the day's general list. They added
 * the act's bumper three to eight times. They put one bumper on the first slot.
 * Sometimes they removed one media, to keep the length of the list.
 *
 * This file names the copy and places the bumpers. It builds no row and it
 * writes nothing. `ComposeState` owns the shape of a row, and it calls these
 * two functions. The order comes from the shared spread, so a copy follows the
 * same rules as every other list.
 */

import { spread } from '../../../../shared/playlist-spread'

/** The word the operators use for a general list, in every name in the data. */
const GENERAL = /general/i

/**
 * The slug of an act, from the name of its bumper.
 *
 * A bumper is named after the scene and the act, as in `Orange_LittleSimz`. The
 * scene is already known from the page, so the first part of the name goes. A
 * name of one part is the act itself and is kept whole.
 */
function actSlug (bumperName) {
  const parts = (bumperName || '').split('_').filter(Boolean)
  const act = parts.length > 1 ? parts.slice(1).join('_') : parts.join('_')

  return act.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * The name of the derived playlist.
 *
 * Every general list in the data has `general` in its name, and the takeover of
 * that day has the act in the same place. The slug therefore takes the place of
 * the word. A name without the word gets the slug added.
 */
function takeoverName (baseName, bumperName) {
  const base = (baseName || '').trim()
  const slug = actSlug(bumperName)

  if (!slug) {
    return base
  }

  if (!base) {
    return slug
  }

  return GENERAL.test(base) ? base.replace(GENERAL, slug) : `${base}_${slug}`
}

/** The first slot no pin holds, so the bumper takes a slot that is free. */
function firstFreeSlot (pins, length) {
  const taken = new Set(Object.keys(pins).map(Number))

  for (let slot = 0; slot < length; slot++) {
    if (!taken.has(slot)) {
      return slot
    }
  }

  return null
}

/**
 * The rows of a takeover, in the order it will play.
 *
 * `rows` are the rows of the general list, already copied and with the replaced
 * media removed. `bumperRows` are the rows of the act. `pins` are the pins the
 * operator set, by slot. `first` asks for a bumper on the first slot, which is
 * what every takeover in the data does. A pin already on the first slot keeps
 * it, and the bumper then takes the first slot that is free.
 *
 * @returns {object} `{ rows, pins }`, with the pins the copy keeps
 */
function placeBumpers (rows, bumperRows, { first = true, pins = {}, seed = 1 } = {}) {
  const all = [...rows, ...bumperRows]
  const kept = { ...pins }

  if (first && bumperRows.length > 0) {
    const slot = firstFreeSlot(kept, all.length)

    if (slot !== null) {
      kept[slot] = bumperRows[0].id
    }
  }

  return { rows: spread(all, { seed, pins: kept }), pins: kept }
}

export { actSlug, takeoverName, placeBumpers }
